import React, { useMemo } from "react";
import { Link } from "react-router-dom";

const clean = (value) => String(value ?? "").trim();

const hasText = (value) => clean(value).length > 0;

const replaceSlug = (href, slug) => {
  const s = clean(slug);
  if (!href) return "";
  return String(href).split("{slug}").join(s);
};

const normalizeHref = (raw, pageSlug = "") => {
  const v0 = clean(raw);
  if (!v0) return "";

  const v = replaceSlug(v0, pageSlug);

  if (v.startsWith("#")) return v;

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  const low = v.toLowerCase();
  if (low.startsWith("wa:") || low.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (low.startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;

  return `/${v}`;
};

const goToLink = (raw, pageSlug = "") => {
  const href = normalizeHref(raw, pageSlug);
  if (!href) return;

  if (href.startsWith("#")) {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = clean(style?.bgGradientFrom);
    const via = clean(style?.bgGradientVia);
    const to = clean(style?.bgGradientTo);
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const iconFor = (type) => {
  const t = clean(type).toLowerCase();
  switch (t) {
    case "instagram":
      return "📸";
    case "facebook":
      return "📘";
    case "tiktok":
      return "🎵";
    case "youtube":
      return "▶️";
    case "x":
    case "twitter":
      return "✖️";
    case "linkedin":
      return "💼";
    case "telegram":
      return "✈️";
    case "whatsapp":
      return "💬";
    default:
      return "🔗";
  }
};

const pickAlign = (config, direction) => {
  const raw = clean(config?.alignment).toLowerCase();
  if (raw === "left" || raw === "center" || raw === "right") return raw;
  return direction === "rtl" ? "right" : "left";
};

export default function FooterSection1({ config = {}, style = {}, general = {}, pageSlug = "" }) {
  const enabled = config?.enabled !== false;

  const direction = clean(config?.direction || "ltr").toLowerCase();
  const rtl = direction === "rtl";
  const align = pickAlign(config, direction);

  const titleColor = style?.titleColor || "#ffffff";
  const subtitleColor = style?.subtitleColor || "rgba(255,255,255,0.85)";
  const linkColor = style?.linkColor || "#93C5FD";
  const dividerColor = style?.dividerColor || "rgba(255,255,255,0.15)";

  const textAlignClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

  const justifyRow =
    align === "center"
      ? "justify-center"
      : align === "left"
      ? rtl
        ? "justify-end"
        : "justify-start"
      : rtl
      ? "justify-start"
      : "justify-end";

  const bg = buildBg(style, "bg-gray-900");
  const footerStyle = { ...(bg.style || {}), color: subtitleColor };

  const businessName = clean(config.businessName);
  const businessDescription = clean(config.businessDescription);
  const businessLogo = clean(config.businessLogo || general?.logoUrl);

  const socials = useMemo(() => {
    const arr = Array.isArray(config.socials) ? config.socials : [];
    return arr
      .map((s) => {
        const type = clean(s?.platform || s?.type);
        const url = clean(s?.url);
        const label = clean(s?.label) || type;
        const iconUrl = clean(s?.iconUrl);
        const id = s?.id || `${type}-${url}`;
        return { id, type, url, label, iconUrl };
      })
      .filter((s) => hasText(s.url));
  }, [config.socials]);

  const privacyLink = useMemo(() => {
    if (hasText(config.privacyLink)) return normalizeHref(config.privacyLink, pageSlug);
    if (pageSlug) return `/${pageSlug}/privacy-policy`;
    return "/privacy-policy";
  }, [config.privacyLink, pageSlug]);

  const privacyText = clean(config.privacyLinkText || config.privacyText) || "Privacy Policy";

  const locationTitle = clean(config.locationTitle);
  const locationText = clean(config.locationText);

  const addressLines = [
    clean(config.addressLine1),
    clean(config.addressLine2),
    clean([clean(config.city), clean(config.country)].filter(Boolean).join(", ")),
  ].filter(Boolean);

  const phone = clean(config.phone);
  const email = clean(config.email);

  const hasLocationBlock =
    hasText(locationTitle) ||
    hasText(locationText) ||
    addressLines.length > 0 ||
    hasText(phone) ||
    hasText(email);

  const legalTitle = clean(config.legalTitle);

  const developerLink = clean(config.developerWebsite || config.developerLink);
  const developerLogo = clean(config.developerLogo);
  const developerName = clean(config.developerName);
  const developedByText = clean(config.developedByText);

  const hasDeveloperBlock =
    hasText(developedByText) ||
    hasText(developerName) ||
    hasText(developerLink) ||
    hasText(developerLogo);

  const hasBrandBlock =
    hasText(businessName) ||
    hasText(businessDescription) ||
    hasText(businessLogo) ||
    socials.length > 0;

  const copyrightLeft = clean(config.copyrightLeft);
  const copyrightRight = clean(config.copyrightRight || config.copyrightText);

  if (!enabled) return null;

  return (
    <footer dir={direction} style={footerStyle} className={`px-4 pt-14 pb-10 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className={`md:col-span-5 ${textAlignClass}`}>
            {hasBrandBlock ? (
              <>
                {(hasText(businessLogo) || hasText(businessName) || hasText(businessDescription)) ? (
                  <div className={`flex items-center gap-3 ${justifyRow}`}>
                    {hasText(businessLogo) ? (
                      <img
                        src={businessLogo}
                        alt="Business logo"
                        className="w-12 h-12 rounded-xl object-cover"
                      />
                    ) : null}

                    {(hasText(businessName) || hasText(businessDescription)) ? (
                      <div className="min-w-0">
                        {hasText(businessName) ? (
                          <div style={{ color: titleColor }} className="text-xl font-bold truncate">
                            {businessName}
                          </div>
                        ) : null}

                        {hasText(businessDescription) ? (
                          <div className="text-sm opacity-90">{businessDescription}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {socials.length ? (
                  <div className={`flex flex-wrap gap-2 ${justifyRow} ${
                    hasText(businessLogo) || hasText(businessName) || hasText(businessDescription)
                      ? "mt-6"
                      : ""
                  }`}>
                  {socials.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => goToLink(s.url, pageSlug)}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition"
                      title={s.type || "social"}
                      type="button"
                    >
                      {s.iconUrl ? (
                        <img
                          src={s.iconUrl}
                          alt={s.label || s.type || "social"}
                          className="w-5 h-5 object-contain rounded-sm"
                        />
                      ) : (
                        <span className="text-lg">{iconFor(s.type)}</span>
                      )}
                      <span className="text-sm">{s.label || "Link"}</span>
                    </button>
                  ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          {/* Location */}
          <div className={`md:col-span-4 ${textAlignClass}`}>
            {hasLocationBlock ? (
              <>
                {hasText(locationTitle) ? (
                  <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
                    {locationTitle}
                  </div>
                ) : null}

                {(hasText(locationText) || addressLines.length > 0) ? (
                  <div className="text-sm space-y-1">
                    {hasText(locationText)
                      ? <div>{locationText}</div>
                      : addressLines.map((line, idx) => <div key={idx}>{line}</div>)}
                  </div>
                ) : null}

                {(hasText(phone) || hasText(email)) ? (
                  <div className={`${hasText(locationText) || addressLines.length > 0 ? "mt-4" : ""} space-y-2 text-sm`}>
                    {hasText(phone) ? (
                      <div>
                        <span style={{ color: titleColor }} className="font-medium">
                          Phone:
                        </span>{" "}
                        <span dir="ltr" className="inline-block">
                          <a
                            className="underline decoration-white/30 hover:decoration-white"
                            href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}
                          >
                            {phone}
                          </a>
                        </span>
                      </div>
                    ) : null}

                    {hasText(email) ? (
                      <div>
                        <span style={{ color: titleColor }} className="font-medium">
                          Email:
                        </span>{" "}
                        <span dir="ltr" className="inline-block">
                          <a
                            className="underline decoration-white/30 hover:decoration-white"
                            href={`mailto:${email}`}
                          >
                            {email}
                          </a>
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          {/* Legal + Developer */}
          <div className={`md:col-span-3 ${textAlignClass}`}>
            {hasText(legalTitle) ? (
              <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
                {legalTitle}
              </div>
            ) : null}

            {/* Privacy always shown */}
            <Link
              to={privacyLink}
              state={{ footerConfig: config }}
              style={{ color: linkColor }}
              className="text-sm underline decoration-white/20 hover:decoration-white transition"
            >
              {privacyText}
            </Link>

            {hasDeveloperBlock ? (
              <div className="mt-8">
                {hasText(developedByText) ? (
                  <div style={{ color: titleColor }} className="font-semibold text-base mb-3">
                    {developedByText}
                  </div>
                ) : null}

                {(hasText(developerName) || hasText(developerLink) || hasText(developerLogo)) ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (hasText(developerLink)) goToLink(developerLink, pageSlug);
                    }}
                    className={`flex items-center gap-3 p-3 rounded-2xl bg-white/10 hover:bg-white/15 transition w-full ${
                      align === "center" ? "justify-center" : "justify-start"
                    } ${!hasText(developerLink) ? "cursor-default" : ""}`}
                  >
                    {hasText(developerLogo) ? (
                      <img
                        src={developerLogo}
                        alt="Developer logo"
                        className="w-10 h-10 rounded-xl object-cover"
                      />
                    ) : null}

                    {(hasText(developerName) || hasText(developerLink)) ? (
                      <div className="min-w-0">
                        {hasText(developerName) ? (
                          <div style={{ color: titleColor }} className="text-sm font-semibold truncate">
                            {developerName}
                          </div>
                        ) : null}

                        {hasText(developerLink) ? (
                          <div className="text-xs opacity-90 truncate" dir="ltr">
                            {developerLink}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {(hasText(copyrightLeft) || hasText(copyrightRight)) ? (
          <>
            <div className="mt-10" style={{ borderTop: `1px solid ${dividerColor}` }} />

            <div
              className={`mt-6 text-xs opacity-90 flex flex-col sm:flex-row flex-wrap gap-2 ${
                align === "center" ? "sm:justify-center" : "sm:justify-between"
              } ${textAlignClass}`}
            >
              {hasText(copyrightLeft) ? <div>{copyrightLeft}</div> : null}
              {hasText(copyrightRight) ? <div>{copyrightRight}</div> : null}
            </div>
          </>
        ) : null}
      </div>
    </footer>
  );
}